/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { QueryState } from '../../components/ui/query-state.js';
import { useContacts, useStartThread } from '../../lib/queries/messaging.js';

interface NewMessageModalProps {
  readonly onCreated: (threadId: string) => void;
  readonly onCancel: () => void;
}

const SELECT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

/** Contact picker that opens (or reuses) a conversation (Figma "New message"). */
export function NewMessageModal({ onCreated, onCancel }: NewMessageModalProps): JSX.Element {
  const { t } = useTranslation();
  const contacts = useContacts();
  const start = useStartThread();
  const [recipientId, setRecipientId] = useState('');

  const onPick = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setRecipientId(e.target.value);
  }, []);

  const onStart = useCallback(() => {
    if (recipientId === '') {
      return;
    }
    start.mutate(recipientId, {
      onSuccess: (result) => {
        onCreated(result.threadId);
      },
    });
  }, [recipientId, start, onCreated]);

  return (
    <div className="space-y-4">
      <QueryState
        isLoading={contacts.isLoading}
        isError={contacts.isError}
        isEmpty={(contacts.data ?? []).length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('messages.noContacts')}
      >
        <select
          className={SELECT_CLASS}
          value={recipientId}
          onChange={onPick}
          aria-label={t('messages.selectContact')}
        >
          <option value="" disabled>
            {t('messages.selectContact')}
          </option>
          {(contacts.data ?? []).map((c) => (
            <option key={c.userId} value={c.userId}>
              {c.fullName}
            </option>
          ))}
        </select>
      </QueryState>

      {start.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={onStart} disabled={recipientId === '' || start.isPending}>
          {t('messages.startChat')}
        </Button>
      </div>
    </div>
  );
}
